import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Chip, CircularProgress } from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import WaterIcon from '@mui/icons-material/Water';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import GrassIcon from '@mui/icons-material/Grass';
import ShieldIcon from '@mui/icons-material/Shield';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import InventoryIcon from '@mui/icons-material/Inventory';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

/**
 * Step configuration — maps each lifecycle step to a "Next Required Action" card.
 *
 * Each entry provides:
 *   title       – card heading
 *   explanation – brief description
 *   cta         – call-to-action button label
 *   route       – navigation target on CTA click
 *   badge       – status chip label
 *   badgeColor  – tailwind colour token for badge
 *   icon        – MUI icon element
 *   accent      – tailwind border / highlight colour
 */
const STEP_CONFIG = {
  1: {
    title: 'KYC Pending',
    explanation: 'Complete your KYC verification to unlock all features and start your farming journey.',
    cta: 'Complete KYC',
    route: '/farmer/kyc',
    badge: 'Pending',
    badgeColor: 'warning',
    icon: <VerifiedUserIcon sx={{ fontSize: 36, color: '#f59e0b' }} />,
    accent: 'border-amber-400',
  },
  2: {
    title: 'Create Your Farm',
    explanation: 'Register your farm details — location, area, and CAA license — to begin.',
    cta: 'Create Farm',
    route: '/farmer/farms',
    badge: 'Action Required',
    badgeColor: 'info',
    icon: <AgricultureIcon sx={{ fontSize: 36, color: '#2563eb' }} />,
    accent: 'border-blue-500',
  },
  3: {
    title: 'Add a Pond',
    explanation: 'Add a pond to your farm so you can start stocking and apply for insurance.',
    cta: 'Add Pond',
    route: '/farmer/ponds',
    badge: 'Action Required',
    badgeColor: 'info',
    icon: <WaterIcon sx={{ fontSize: 36, color: '#0891b2' }} />,
    accent: 'border-cyan-500',
  },
  4: {
    title: 'Apply for Credit',
    explanation: 'Get credit to fund hatchery seeds, feed, and other cultivation inputs.',
    cta: 'Apply for Credit',
    route: '/farmer/requests',
    badge: 'Available',
    badgeColor: 'success',
    icon: <AccountBalanceIcon sx={{ fontSize: 36, color: '#059669' }} />,
    accent: 'border-emerald-500',
  },
  5: {
    title: 'Credit Under Review',
    explanation: 'Your credit application is being reviewed. You will be notified once approved.',
    cta: 'View Status',
    route: '/farmer/requests',
    badge: 'Processing',
    badgeColor: 'warning',
    icon: <HourglassEmptyIcon sx={{ fontSize: 36, color: '#d97706' }} />,
    accent: 'border-yellow-500',
  },
  6: {
    title: 'Add Stocking Event',
    explanation: 'Stock your pond with seeds to begin the crop cycle and activate insurance eligibility.',
    cta: 'Add Stocking',
    route: '/farmer/stocking-details',
    badge: 'Action Required',
    badgeColor: 'info',
    icon: <GrassIcon sx={{ fontSize: 36, color: '#16a34a' }} />,
    accent: 'border-green-500',
  },
  7: {
    title: 'Activate Insurance',
    explanation: 'Protect your crop by creating an insurance policy for your stocked pond.',
    cta: 'Create Policy',
    route: '/farmer/policies',
    badge: 'Available',
    badgeColor: 'success',
    icon: <ShieldIcon sx={{ fontSize: 36, color: '#7c3aed' }} />,
    accent: 'border-violet-500',
  },
  8: {
    title: 'Crop Dashboard',
    explanation: 'Your crop is active. Monitor DOC, feed logs, and water quality regularly.',
    cta: 'View Crop',
    route: '/farmer/ponds',
    badge: 'Active',
    badgeColor: 'success',
    icon: <MonitorHeartIcon sx={{ fontSize: 36, color: '#0d9488' }} />,
    accent: 'border-teal-500',
  },
  9: {
    title: 'Record Harvest',
    explanation: 'Your crop cycle is maturing. Record harvest details when ready.',
    cta: 'Record Harvest',
    route: '/farmer/requests',
    badge: 'Ready',
    badgeColor: 'info',
    icon: <InventoryIcon sx={{ fontSize: 36, color: '#2563eb' }} />,
    accent: 'border-blue-500',
  },
  10: {
    title: 'Settlement Summary',
    explanation: 'Your harvest has been recorded. View settlement deductions and payout details.',
    cta: 'View Settlement',
    route: '/farmer/dashboard',
    badge: 'Processing',
    badgeColor: 'warning',
    icon: <ReceiptLongIcon sx={{ fontSize: 36, color: '#d97706' }} />,
    accent: 'border-amber-500',
  },
  11: {
    title: 'Cycle Complete — Profit Summary',
    explanation: 'Congratulations! Your farming cycle is complete. Review your profit and plan the next season.',
    cta: 'View Profit',
    route: '/farmer/dashboard',
    badge: 'Complete',
    badgeColor: 'success',
    icon: <EmojiEventsIcon sx={{ fontSize: 36, color: '#ca8a04' }} />,
    accent: 'border-yellow-400',
  },
};

/**
 * NextActionCard
 *
 * Renders a dynamic "Next Required Action" card based on the farmer's
 * current lifecycle step.  Includes title, explanation, CTA button,
 * and status badge.
 *
 * @param {{ currentStep: number, loading?: boolean }} props
 */
const NextActionCard = ({ currentStep = 1, loading = false }) => {
  const navigate = useNavigate();
  const config = STEP_CONFIG[currentStep] || STEP_CONFIG[1];

  if (loading) {
    return (
      <div className="w-full rounded-2xl border border-gray-200 bg-white p-6 flex items-center justify-center min-h-[140px]">
        <CircularProgress size={28} />
      </div>
    );
  }

  return (
    <div
      className={`w-full rounded-2xl border-l-4 ${config.accent} bg-white shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden`}
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-5">
        {/* Icon */}
        <div className="flex-shrink-0 flex items-center justify-center w-14 h-14 rounded-xl bg-gray-50">
          {config.icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-lg font-bold text-gray-900 leading-tight">
              {config.title}
            </h3>
            <Chip
              label={config.badge}
              color={config.badgeColor}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: '0.7rem', height: 24 }}
            />
          </div>
          <p className="text-sm text-gray-500 leading-relaxed">
            {config.explanation}
          </p>
        </div>

        {/* CTA */}
        <button
          onClick={() => navigate(config.route)}
          className="flex-shrink-0 flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold text-sm shadow-sm transition-all duration-200 whitespace-nowrap"
        >
          {config.cta}
          <ArrowForwardIcon sx={{ fontSize: 16 }} />
        </button>
      </div>
    </div>
  );
};

export default NextActionCard;
